import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert,
  Button,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material/styles';
import Input from '../../ui/forms/fields/input';
import { getIfaCodes } from '../../../features/ifaCodes/ifaCodes.slice';
import {
  getAdvisors,
  mappAdvisorCode,
  removeAdvisorCode
} from '../../../features/advisor/advisor.slice';
import TableSortLabel from '@mui/material/TableSortLabel';

const AdvisorMappings = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [mancoFilter, setMancoFilter] = useState('all');
  const [advisorFilter, setAdvisorFilter] = useState('all');
  const [alert, setAlert] = useState(null);

  const ifaCodes = useSelector((state) => state.ifaCode.ifaCodes);
  const advisors = useSelector((state) => state.advisor.advisors);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    dispatch(getIfaCodes());
    dispatch(getAdvisors());
  }, [dispatch]);

  const findAdvisorById = (advisorId) => advisors.find((advisor) => advisor._id === advisorId);

  const handleAdvisorChange = (ifa, selectedAdvisorId) => {
    if (!selectedAdvisorId) return;

    const advisor = findAdvisorById(selectedAdvisorId);
    if (!advisor) return;

    const existingMapping = advisors.find((adv) =>
      adv.advisor?.mapped?.some((entry) => entry.ifaCodeId === ifa._id)
    );

    if (existingMapping) {
      setAlert({ ifa, selectedAdvisorId, prevAdvisorId: existingMapping._id });
    } else {
      dispatch(
        mappAdvisorCode({
          id: selectedAdvisorId,
          ifaCode: ifa._id,
          provider: ifa.serviceprovider._id
        })
      )
        .unwrap()
        .then(() => {
          dispatch(getAdvisors());
          dispatch(getIfaCodes());
        })
        .catch((error) => {
          console.error('Error mapping advisor:', error);
        });
    }
  };

  const confirmAdvisorChange = async () => {
    if (!alert) return;

    const { ifa, selectedAdvisorId, prevAdvisorId, action } = alert;

    const payload = {
      id: selectedAdvisorId,
      ifaCode: ifa._id,
      provider: ifa.serviceprovider._id,
      ...(prevAdvisorId && { prevAdvisor: prevAdvisorId })
    };

    try {
      if (action === 'unmap') {
        await dispatch(removeAdvisorCode(payload));
      } else {
        await dispatch(mappAdvisorCode(payload));
      }

      // Always dispatch after action
      dispatch(getAdvisors());
      dispatch(getIfaCodes());
    } catch (error) {
      console.error(`Error ${action === 'unmap' ? 'unmapping' : 'mapping'} advisor:`, error);
    }

    setAlert(null);
  };

  const advisorIsMappedRecord = (advisorId, ifa) => {
    // Get the advisor ID. Check if the advisor selected id is mapped on the record in the mapped cell.
    // If the advisor is mapped, return true. Else, return false.
    if (!advisorId) return false;

    const advisor = advisors.find((adv) => adv._id === advisorId);

    return advisor && advisor.advisor?.mapped?.some((entry) => entry.ifaCodeId === ifa._id);
  };

  const filteredIfaCodes = useMemo(() => {
    const sortedData = [...ifaCodes].sort((a, b) => {
      const aValue =
        sortConfig.key === 'serviceprovider.name' ? a.serviceprovider?.name || '' : a.advisor || '';
      const bValue =
        sortConfig.key === 'serviceprovider.name' ? b.serviceprovider?.name || '' : b.advisor || '';

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sortedData.filter((ifa) => {
      const searchLower = search.toLowerCase();
      return (
        (mancoFilter.toLowerCase() === 'all' ||
          (ifa.serviceprovider?.name || '').toLowerCase() === mancoFilter.toLowerCase()) &&
        (advisorFilter.toLowerCase() === 'all' || advisorIsMappedRecord(advisorFilter, ifa)) &&
        (search === '' ||
          (ifa.serviceprovider?.name || '').toLowerCase().includes(searchLower) ||
          (ifa.advisor || '').toLowerCase().includes(searchLower) ||
          (ifa.code || '').toLowerCase().includes(searchLower))
      );
    });
  }, [ifaCodes, sortConfig, search, mancoFilter, advisorFilter]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedData = [...(filteredIfaCodes || [])].sort((a, b) => {
      const aValue = key === 'serviceprovider.name' ? a.serviceprovider?.name : a.advisor;
      const bValue = key === 'serviceprovider.name' ? b.serviceprovider?.name : b.advisor;

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
  };

  return (
    <Box p={4}>
      <TableContainer component={Paper} sx={{ padding: 3, borderRadius: 5 }}>
        <Box mb={3}>
          <Typography variant="h2" fontWeight={500} gutterBottom>
            Advisor Mapping
          </Typography>
          <Typography variant="subtitle1" fontSize={'20px'}>
            Please link your advisors to their original profile set up by completing the ‘Mapped to’
            fields.
          </Typography>
        </Box>

        <Box display={'flex'} justifyContent={'flex-end'}>
          <Box display="flex" gap={2} my={2} alignItems={'center'} width={'60%'}>
            <Input
              label="Search"
              variant="outlined"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Manco/LISP Name</InputLabel>
              <Select
                value={mancoFilter}
                onChange={(e) => setMancoFilter(e.target.value)}
                label="Manco/LISP Name">
                <MenuItem value="all">All</MenuItem>
                {[...new Set(ifaCodes.map((ifa) => ifa.serviceprovider?.name))]
                  .sort((a, b) => a.localeCompare(b))
                  .map((manco, index) => (
                    <MenuItem key={index} value={manco}>
                      {manco}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Advisor Name</InputLabel>
              <Select
                value={advisorFilter}
                onChange={(e) => setAdvisorFilter(e.target.value)}
                label="Advisor Name">
                <MenuItem value="all">All</MenuItem>
                {advisors.map((advisor) => (
                  <MenuItem key={advisor._id} value={advisor._id}>
                    {advisor.firstName} {advisor.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'serviceprovider.name'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('serviceprovider.name')}>
                  Manco/LISP Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '25%' }}>Code</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'advisor'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('advisor')}>
                  Advisor
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '25%' }}>Mapped To</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredIfaCodes.map((ifa, index) => (
              <TableRow key={index}>
                <TableCell>{ifa.serviceprovider?.name}</TableCell>
                <TableCell>{ifa.code}</TableCell>
                <TableCell>{ifa.advisor}</TableCell>
                <TableCell>
                  <Box sx={{ minHeight: 40, width: '100%' }}>
                    {alert && alert.ifa._id === ifa._id ? (
                      <Alert
                        severity="warning"
                        icon={false}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
                        action={
                          <Box sx={{ width: '100%', textAlign: 'right', mt: 1 }}>
                            <Button color="inherit" size="small" onClick={() => setAlert(null)}>
                              Undo
                            </Button>
                            <Button color="inherit" size="small" onClick={confirmAdvisorChange}>
                              Confirm
                            </Button>
                          </Box>
                        }>
                        {alert.action === 'unmap'
                          ? ' This code is currently mapped to an advisor. Are you sure you want to remove it from the advisor? Please confirm.'
                          : ' This code is already mapped to an advisor. Are you sure you want to map it to a new advisor? Please confirm.'}
                      </Alert>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <FormControl variant="outlined" size="small" fullWidth>
                          <Select
                            value={
                              advisors.find((advisor) =>
                                advisor.advisor?.mapped?.some(
                                  (entry) => entry.ifaCodeId === ifa._id
                                )
                              )?._id || ''
                            }
                            onChange={(e) => handleAdvisorChange(ifa, e.target.value)}
                            displayEmpty>
                            <MenuItem value="" disabled>
                              Map to
                            </MenuItem>
                            {advisors.map((advisor) => (
                              <MenuItem key={advisor._id} value={advisor._id}>
                                {advisor.firstName} {advisor.lastName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {advisors.find((advisor) =>
                          advisor.advisor?.mapped?.some((entry) => entry.ifaCodeId === ifa._id)
                        ) && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              setAlert({
                                ifa,
                                selectedAdvisorId: advisors.find((advisor) =>
                                  advisor.advisor?.mapped?.some(
                                    (entry) => entry.ifaCodeId === ifa._id
                                  )
                                )._id,
                                action: 'unmap'
                              })
                            }>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdvisorMappings;
